import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Check, CheckCircle2, LogOut, Maximize2, Minimize2, ShieldAlert } from "lucide-react";
import { useAuth } from "@/modules/auth/infrastructure/react/auth-provider";
import {
  useAcceptTermsMutation,
  useCurrentTermsQuery,
  usePendingTermsQuery,
} from "../../infrastructure/queries/terms.queries";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/ui/dialog";
import { Button } from "@/shared/ui/button";
import { Checkbox } from "@/shared/ui/checkbox";
import { Badge } from "@/shared/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/shared/ui/tabs";
import { Spinner } from "@/shared/ui/spinner";
import { useToast } from "@/shared/hooks/use-toast";
import { TermsType, type PendingTerm } from "../../domain/entities";
import { getDateLocale } from "@/shared/lib/format";

import { formatTermsContent } from "../format-terms";

export function TermsAcceptanceModal() {
  const { t, i18n } = useTranslation(["terms"]);
  const { isAuthenticated, logout } = useAuth();
  const { toast } = useToast();

  const { data: pendingTerms = [] } = usePendingTermsQuery({
    enabled: isAuthenticated,
  });

  const { data: currentTerms = [], isLoading: isLoadingCurrent } =
    useCurrentTermsQuery();

  const acceptMutation = useAcceptTermsMutation();

  const [activeTab, setActiveTab] = useState<string>("");
  const [agreedTypes, setAgreedTypes] = useState<Record<string, boolean>>({});
  const [isExpanded, setIsExpanded] = useState<boolean>(false);

  const hasPending = isAuthenticated && pendingTerms.length > 0;

  useEffect(() => {
    if (pendingTerms.length > 0) {
      if (!activeTab || !pendingTerms.some((p) => p.type === activeTab)) {
        setActiveTab(pendingTerms[0].type);
      }
    }
  }, [pendingTerms, activeTab]);

  if (!hasPending) {
    return null;
  }

  const getDocForPending = (term: PendingTerm) => {
    return currentTerms.find((doc) => doc.type === term.type);
  };

  const getTermLabel = (type: TermsType) => {
    if (type === TermsType.PrivacyPolicy) {
      return t("terms:modal.tabPrivacyPolicy", "Política de Privacidade");
    }
    return t("terms:modal.tabTermsOfUse", "Termos de Uso");
  };

  const getAgreementLabel = (type: TermsType) => {
    if (type === TermsType.TermsOfUse) {
      return t(
        "terms:modal.agreementCheckboxTermsOfUse",
        "Declaro que li, compreendi e concordo com os Termos de Uso.",
      );
    }
    if (type === TermsType.PrivacyPolicy) {
      return t(
        "terms:modal.agreementCheckboxPrivacyPolicy",
        "Declaro que li, compreendi e concordo com a Política de Privacidade.",
      );
    }
    return t(
      "terms:modal.agreementCheckboxGeneric",
      "Declaro que li, compreendi e concordo com este documento.",
    );
  };

  const allAgreed =
    pendingTerms.length > 0 &&
    pendingTerms.every((term) => !!agreedTypes[term.type]);

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return "";
    try {
      const locale = getDateLocale(i18n.language);
      return new Intl.DateTimeFormat(locale, { dateStyle: "medium" }).format(
        new Date(dateStr),
      );
    } catch {
      return dateStr;
    }
  };

  const handleAccept = async () => {
    if (!allAgreed || pendingTerms.length === 0) return;
    try {
      await acceptMutation.mutateAsync(
        pendingTerms.map((p) => ({ type: p.type, version: p.version })),
      );
      setAgreedTypes({});
      toast({
        title: t("terms:modal.successToast", "Termos atualizados aceitos com sucesso!"),
      });
    } catch {
      toast({
        title: t(
          "terms:modal.errorToast",
          "Ocorreu um erro ao registrar o aceite dos termos. Tente novamente.",
        ),
        variant: "destructive",
      });
    }
  };

  const handleLogout = async () => {
    await logout();
  };

  const activeTerm =
    pendingTerms.find((p) => p.type === activeTab) || pendingTerms[0];
  const activeDoc = activeTerm ? getDocForPending(activeTerm) : undefined;

  const renderDocumentContent = (term: PendingTerm) => {
    const doc = getDocForPending(term);

    if (isLoadingCurrent && !doc) {
      return (
        <div className="flex flex-col items-center justify-center p-8 text-muted-foreground gap-3">
          <Spinner size="md" />
          <span className="text-xs">
            {t("terms:modal.loadingContent", "Carregando conteúdo atualizado...")}
          </span>
        </div>
      );
    }

    if (!doc) {
      return (
        <div className="p-6 text-center text-sm text-destructive">
          {t("terms:modal.contentError", "Não foi possível carregar o texto completo.")}
        </div>
      );
    }

    return (
      <div className="flex flex-col flex-1 min-h-0">
        {doc.publishedAt && (
          <div className="flex items-center justify-between text-xs px-1 font-medium shrink-0 pb-1">
            <span className="text-muted-foreground dark:text-slate-400 font-medium text-[11px]">
              {t("terms:modal.publishedOn", {
                date: formatDate(doc.publishedAt),
              })}
            </span>
          </div>
        )}
        <div className="relative flex-1 min-h-0 flex flex-col rounded-2xl border border-black/10 dark:border-white/10 bg-white dark:bg-black/40 overflow-hidden shadow-inner">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={() => {
              setActiveTab(term.type);
              setIsExpanded(true);
            }}
            className="absolute top-2.5 right-2.5 z-10 h-7 w-7 rounded-lg bg-background/80 hover:bg-background text-muted-foreground hover:text-foreground backdrop-blur-md border border-black/10 dark:border-white/10 shadow-sm transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            title={t("terms:modal.expandTooltip", "Expandir caixa de texto")}
            aria-label={t("terms:modal.expandButton", "Expandir")}
          >
            <Maximize2 className="h-3.5 w-3.5 text-primary" />
          </Button>
          <div
            tabIndex={0}
            role="region"
            aria-label={doc.title}
            className="flex-1 min-h-0 overflow-y-auto p-4 sm:p-5 pr-11 text-xs sm:text-sm leading-relaxed whitespace-pre-wrap font-normal text-slate-800 dark:text-slate-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50"
          >
            {formatTermsContent(doc.content)}
          </div>
        </div>

        <div className="shrink-0 flex items-center gap-2.5 pt-2 pb-0.5 select-none group">
          <Checkbox
            id={`terms-agreement-${term.type}`}
            checked={!!agreedTypes[term.type]}
            onCheckedChange={(checked) =>
              setAgreedTypes((prev) => ({
                ...prev,
                [term.type]: !!checked,
              }))
            }
            className="h-5 w-5 rounded-full border-2 border-primary data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground shrink-0 transition-transform group-hover:scale-105 cursor-pointer"
          />
          <label
            htmlFor={`terms-agreement-${term.type}`}
            className="text-xs sm:text-sm font-semibold text-foreground/90 group-hover:text-foreground apple-glass-text-primary leading-snug cursor-pointer select-none"
          >
            {getAgreementLabel(term.type)}
          </label>
        </div>
      </div>
    );
  };

  return (
    <Dialog open={true}>
      <DialogContent
        showCloseButton={false}
        overlayClassName="backdrop-blur-md bg-black/75"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => {
          e.preventDefault();
          if (isExpanded) {
            setIsExpanded(false);
          }
        }}
        className="fixed left-[50%] top-[50%] -translate-x-1/2 -translate-y-1/2 w-[95vw] max-w-2xl sm:max-w-3xl h-[85vh] max-h-[680px] flex flex-col gap-2 p-5 sm:p-6 overflow-hidden rounded-3xl shadow-2xl border border-white/20 dark:border-white/10 bg-background/95 dark:bg-[#0c1017]/95 backdrop-blur-2xl ring-1 ring-black/5 dark:ring-white/10"
      >
        <DialogHeader className="shrink-0 space-y-1.5 text-left">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 sm:h-10 sm:w-10 rounded-xl bg-primary/10 border border-primary/20 text-primary flex items-center justify-center shrink-0 shadow-sm">
              <ShieldAlert className="h-5 w-5 text-primary" />
            </div>
            <div>
              <Badge
                variant="outline"
                className="text-primary border-primary/30 bg-primary/10 text-[10px] font-black tracking-wider uppercase px-2 py-0.5 rounded-full mb-0.5 inline-block"
              >
                {t("terms:modal.badge", "Aviso Obrigatório")}
              </Badge>
              <DialogTitle className="text-lg sm:text-xl font-black tracking-tight text-foreground apple-glass-text-primary">
                {t("terms:modal.title", "Atualização dos Termos de Serviço")}
              </DialogTitle>
            </div>
          </div>
          <DialogDescription className="text-xs sm:text-sm font-medium text-muted-foreground dark:text-slate-300 apple-glass-text-secondary leading-snug pt-0.5">
            {t(
              "terms:modal.description",
              "Nossos termos de uso e políticas foram atualizados para garantir maior transparência, segurança e conformidade regulatória. Para continuar utilizando a plataforma Avizo, é necessário ler e aceitar os documentos pendentes abaixo.",
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
          {pendingTerms.length === 1 ? (
            renderDocumentContent(pendingTerms[0])
          ) : (
            <Tabs
              value={activeTab}
              onValueChange={setActiveTab}
              className="flex-1 min-h-0 flex flex-col overflow-hidden"
            >
              <TabsList
                className="shrink-0 grid w-full p-1 bg-black/5 dark:bg-white/5 rounded-2xl border border-black/10 dark:border-white/10 mb-1.5"
                style={{ gridTemplateColumns: `repeat(${pendingTerms.length}, minmax(0, 1fr))` }}
              >
                {pendingTerms.map((term) => (
                  <TabsTrigger
                    key={term.type}
                    value={term.type}
                    className="group rounded-xl font-bold py-1.5 text-xs sm:text-sm transition-all data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-sm flex items-center justify-center gap-1.5"
                  >
                    <span>{getTermLabel(term.type)}</span>
                    {agreedTypes[term.type] && (
                      <Check className="h-3.5 w-3.5 stroke-[3] text-emerald-500 group-data-[state=active]:text-primary-foreground" />
                    )}
                  </TabsTrigger>
                ))}
              </TabsList>
              {pendingTerms.map((term) => (
                <TabsContent
                  key={term.type}
                  value={term.type}
                  className="flex-1 min-h-0 flex flex-col mt-0 data-[state=inactive]:hidden overflow-hidden"
                >
                  {renderDocumentContent(term)}
                </TabsContent>
              ))}
            </Tabs>
          )}
        </div>

        <DialogFooter className="shrink-0 flex flex-col-reverse sm:flex-row items-center justify-between gap-2.5 border-t border-black/10 dark:border-white/10 pt-2.5">
          <Button
            type="button"
            variant="outline"
            onClick={handleLogout}
            disabled={acceptMutation.isPending}
            className="w-full sm:w-auto h-10 px-4 sm:px-5 rounded-xl border-black/10 dark:border-white/10 hover:bg-destructive/10 hover:text-destructive hover:border-destructive/30 font-semibold text-xs sm:text-sm transition-all"
          >
            <LogOut className="h-4 w-4 mr-1.5" />
            {t("terms:modal.logoutButton", "Recusar e sair")}
          </Button>

          <Button
            type="button"
            disabled={!allAgreed || acceptMutation.isPending || isLoadingCurrent}
            onClick={handleAccept}
            className="w-full sm:w-auto h-10 px-5 sm:px-6 rounded-xl bg-primary text-primary-foreground hover:bg-primary/90 font-bold text-xs sm:text-sm shadow-lg shadow-primary/25 disabled:shadow-none transition-all"
          >
            {acceptMutation.isPending ? (
              <>
                <Spinner size="sm" className="mr-2" />
                {t("terms:modal.acceptingButton", "Processando...")}
              </>
            ) : (
              <>
                <CheckCircle2 className="h-4 w-4 mr-1.5" />
                {t("terms:modal.acceptButton", "Aceitar e continuar")}
              </>
            )}
          </Button>
        </DialogFooter>

        {/* Maximized Reading Overlay inside the Modal */}
        {isExpanded && activeDoc && (
          <div className="absolute inset-0 z-30 bg-background dark:bg-[#0c1017] p-3.5 sm:p-5 flex flex-col rounded-3xl animate-in fade-in zoom-in-95 duration-200">
            <div className="flex items-center justify-between pb-2 border-b border-black/10 dark:border-white/10 shrink-0">
              <div>
                <h3 className="text-sm sm:text-base font-bold tracking-tight text-foreground apple-glass-text-primary">
                  {getTermLabel(activeDoc.type)}
                </h3>
                {activeDoc.publishedAt && (
                  <p className="text-[11px] text-muted-foreground dark:text-slate-400 font-medium">
                    {t("terms:modal.publishedOn", {
                      date: formatDate(activeDoc.publishedAt),
                    })}
                  </p>
                )}
              </div>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => setIsExpanded(false)}
                className="h-8 w-8 rounded-lg border border-black/10 dark:border-white/10 hover:bg-black/5 dark:hover:bg-white/5"
                title={t("terms:modal.collapseTooltip", "Recolher caixa de texto")}
                aria-label={t("terms:modal.collapseButton", "Recolher")}
              >
                <Minimize2 className="h-4 w-4 text-primary" />
              </Button>
            </div>

            <div
              tabIndex={0}
              role="region"
              aria-label={activeDoc.title}
              className="flex-1 min-h-0 overflow-y-auto mt-2 p-3 sm:p-4 rounded-2xl border border-black/10 dark:border-white/10 bg-white dark:bg-black/40 text-xs sm:text-sm leading-relaxed whitespace-pre-wrap font-normal text-slate-800 dark:text-slate-100 shadow-inner focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50"
            >
              {formatTermsContent(activeDoc.content)}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
